import React, { useEffect, useMemo, useState } from "react";

/**
 * Shared screens + permissions picker for role create/edit modals.
 */

const PAYMENT_ACTIONS = new Set([
  "pay_cash",
  "pay_card",
  "pay_online",
  "pay_jazzcash",
  "pay_cheque",
  "pay_wallet",
  "pay_other",
]);

const VOUCHER_ACTIONS = new Set([
  "pay_voucher",
  "apply_discount",
  "split_voucher",
  "extend_due_date",
  "reactivate_voucher",
]);

function shortLabel(perm) {
  const raw = String(perm.label || perm.action || perm.key);
  const parts = raw.split(": ");
  return parts.length > 1 ? parts.slice(1).join(": ") : raw;
}

function groupPermissions(permissions) {
  const payment = [];
  const voucher = [];
  const general = [];
  permissions.forEach((perm) => {
    if (PAYMENT_ACTIONS.has(perm.action)) payment.push(perm);
    else if (VOUCHER_ACTIONS.has(perm.action)) voucher.push(perm);
    else general.push(perm);
  });
  return [
    general.length
      ? {
          id: "access",
          title: "Access",
          hint: "Day-to-day actions on this screen.",
          items: general,
        }
      : null,
    voucher.length
      ? {
          id: "voucher",
          title: "Voucher actions",
          hint: "Paying a voucher, discounts, splits, and due dates.",
          items: voucher,
        }
      : null,
    payment.length
      ? {
          id: "payment",
          title: "Payment methods",
          hint: "Checked methods are the only ones this role can use. Leave this group empty to allow every method.",
          items: payment,
        }
      : null,
  ].filter(Boolean);
}

function ActionPill({ label, checked, disabled, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-pressed={checked}
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition ${
        checked
          ? "bg-gx-navy text-white border-gx-navy shadow-sm"
          : "bg-gx-surface text-gx-ink border-gx-border hover:border-gx-navy/40"
      } disabled:cursor-default ${disabled && !checked ? "opacity-60" : ""}`}
    >
      <span
        className={`h-2 w-2 rounded-full ${checked ? "bg-white" : "bg-gx-border"}`}
        aria-hidden="true"
      />
      {label}
    </button>
  );
}

export function RoleScreensAndPermissions({
  screens = [],
  permissionCatalog = [],
  selectedScreens = [],
  selectedPermissions = [],
  onToggleScreen,
  onTogglePermission,
  readOnly = false,
}) {
  const [query, setQuery] = useState("");
  const [activeScreen, setActiveScreen] = useState(null);

  const permissionsByScreen = useMemo(() => {
    const map = new Map();
    permissionCatalog.forEach((perm) => {
      const list = map.get(perm.screenKey) || [];
      list.push(perm);
      map.set(perm.screenKey, list);
    });
    return map;
  }, [permissionCatalog]);

  const filteredScreens = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return screens;
    return screens.filter((screen) => {
      const perms = permissionsByScreen.get(screen.key) || [];
      const haystack = [
        screen.label,
        screen.key,
        screen.description,
        ...perms.map((perm) => `${perm.label || ""} ${perm.action || ""}`),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [screens, permissionsByScreen, query]);

  useEffect(() => {
    if (activeScreen && filteredScreens.some((screen) => screen.key === activeScreen)) return;
    const preferred =
      filteredScreens.find((screen) => selectedScreens.includes(screen.key)) ||
      filteredScreens[0];
    setActiveScreen(preferred?.key || null);
  }, [activeScreen, filteredScreens, selectedScreens]);

  const active = screens.find((screen) => screen.key === activeScreen) || null;
  const activePermissions = active ? permissionsByScreen.get(active.key) || [] : [];
  const activeGroups = groupPermissions(activePermissions);
  const activeSelectedCount = activePermissions.filter((perm) =>
    selectedPermissions.includes(perm.key)
  ).length;
  const screenIsOn = active ? selectedScreens.includes(active.key) : false;

  const flipKeys = (keys, turnOn) => {
    keys.forEach((key) => {
      const on = selectedPermissions.includes(key);
      if (turnOn !== on) onTogglePermission(key);
    });
  };

  const focusScreen = (screenKey) => {
    if (!selectedScreens.includes(screenKey) && !readOnly) onToggleScreen(screenKey);
    setActiveScreen(screenKey);
  };

  return (
    <div className="mt-4">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-gx border border-gx-border bg-gx-canvas px-3 py-2">
        <div>
          <p className="text-sm font-semibold text-gx-ink">Access</p>
          <p className="text-xs text-gx-ink-soft">
            {selectedScreens.length} screen{selectedScreens.length === 1 ? "" : "s"} ·{" "}
            {selectedPermissions.length} action{selectedPermissions.length === 1 ? "" : "s"}
          </p>
        </div>
        {!readOnly && selectedScreens.length > 0 && (
          <button
            type="button"
            className="text-sm font-medium text-gx-red hover:underline"
            onClick={() => selectedScreens.forEach((key) => onToggleScreen(key))}
          >
            Clear all
          </button>
        )}
      </div>

      <div className="mt-3 grid gap-3 md:grid-cols-[240px_minmax(0,1fr)]">
        <div className="rounded-gx border border-gx-border bg-gx-surface overflow-hidden">
          <div className="p-2 border-b border-gx-border">
            <label className="sr-only" htmlFor="role-screen-search">
              Search screens or actions
            </label>
            <input
              id="role-screen-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search screens or actions"
              className="w-full rounded-gx-control border border-gx-border bg-gx-canvas px-3 py-2 text-sm outline-0"
            />
          </div>
          <div className="max-h-80 overflow-y-auto p-2 space-y-1">
            {filteredScreens.length === 0 ? (
              <p className="px-2 py-6 text-center text-sm text-gx-ink-soft">No matches.</p>
            ) : (
              filteredScreens.map((screen) => {
                const perms = permissionsByScreen.get(screen.key) || [];
                const selected = selectedScreens.includes(screen.key);
                const count = perms.filter((perm) => selectedPermissions.includes(perm.key)).length;
                const isActive = screen.key === activeScreen;
                return (
                  <div
                    key={screen.key}
                    className={`flex items-stretch rounded-gx-control border transition ${
                      isActive
                        ? "border-gx-navy bg-gx-navy/5"
                        : "border-transparent hover:bg-gx-canvas"
                    }`}
                  >
                    <button
                      type="button"
                      className="min-w-0 flex-1 px-2 py-2 text-left"
                      onClick={() => focusScreen(screen.key)}
                    >
                      <span className="block truncate text-sm font-medium text-gx-ink">
                        {screen.label}
                      </span>
                      <span className="block text-xs text-gx-ink-soft">
                        {selected ? `${count} of ${perms.length} on` : `${perms.length} actions`}
                      </span>
                    </button>
                    <button
                      type="button"
                      disabled={readOnly}
                      aria-pressed={selected}
                      aria-label={selected ? `Turn off ${screen.label}` : `Turn on ${screen.label}`}
                      onClick={() => !readOnly && onToggleScreen(screen.key)}
                      className={`m-2 h-6 w-11 shrink-0 rounded-full transition relative disabled:cursor-default ${
                        selected ? "bg-gx-navy" : "bg-gx-border"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${
                          selected ? "left-5" : "left-0.5"
                        }`}
                      />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="rounded-gx border border-gx-border bg-gx-surface min-h-[16rem]">
          {!active ? (
            <p className="p-6 text-sm text-gx-ink-soft">Choose a screen to set what this role can do.</p>
          ) : (
            <div className="p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold text-gx-ink">{active.label}</h3>
                  <p className="text-xs text-gx-ink-soft mt-0.5">
                    {screenIsOn
                      ? `${activeSelectedCount} of ${activePermissions.length} actions on`
                      : "Turn this screen on to grant actions. View is selected automatically."}
                  </p>
                </div>
                {!readOnly && screenIsOn && activePermissions.length > 0 && (
                  <div className="flex gap-3 text-sm">
                    <button
                      type="button"
                      className="font-medium text-gx-navy hover:underline"
                      onClick={() => flipKeys(activePermissions.map((perm) => perm.key), true)}
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      className="font-medium text-gx-ink-soft hover:underline"
                      onClick={() => flipKeys(activePermissions.map((perm) => perm.key), false)}
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>

              {!screenIsOn ? (
                <button
                  type="button"
                  disabled={readOnly}
                  onClick={() => onToggleScreen(active.key)}
                  className="mt-4 gx-cta disabled:opacity-60"
                >
                  Enable {active.label}
                </button>
              ) : (
                <div className="mt-4 space-y-4 max-h-72 overflow-y-auto pr-1">
                  {activeGroups.map((group) => {
                    const groupOn = group.items.filter((perm) =>
                      selectedPermissions.includes(perm.key)
                    ).length;
                    return (
                      <section key={group.id}>
                        <div className="flex items-baseline justify-between gap-2">
                          <h4 className="text-sm font-semibold text-gx-ink">{group.title}</h4>
                          {!readOnly && (
                            <button
                              type="button"
                              className="text-xs font-medium text-gx-navy hover:underline"
                              onClick={() =>
                                flipKeys(
                                  group.items.map((perm) => perm.key),
                                  groupOn !== group.items.length
                                )
                              }
                            >
                              {groupOn === group.items.length ? "Clear group" : "Select group"}
                            </button>
                          )}
                        </div>
                        <p className="text-xs text-gx-ink-soft mt-0.5 mb-2">{group.hint}</p>
                        <div className="flex flex-wrap gap-2">
                          {group.items.map((perm) => (
                            <ActionPill
                              key={perm.key}
                              label={shortLabel(perm)}
                              checked={selectedPermissions.includes(perm.key)}
                              disabled={readOnly}
                              onClick={() => !readOnly && onTogglePermission(perm.key)}
                            />
                          ))}
                        </div>
                      </section>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** When enabling a screen, also select its :read permission. */
export function toggleScreenWithRead(prevScreens, prevPermissions, screenKey, permissionCatalog) {
  const enabling = !prevScreens.includes(screenKey);
  const nextScreens = enabling
    ? [...prevScreens, screenKey]
    : prevScreens.filter((k) => k !== screenKey);

  let nextPermissions = [...prevPermissions];
  if (enabling) {
    const readKey = `${screenKey}:read`;
    if (permissionCatalog.some((p) => p.key === readKey) && !nextPermissions.includes(readKey)) {
      nextPermissions.push(readKey);
    }
  } else {
    nextPermissions = nextPermissions.filter((k) => !k.startsWith(`${screenKey}:`));
  }
  return { screens: nextScreens, permissions: nextPermissions };
}
